import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import ApiRequestCreatedPoliciesByUserGraph from '../../api/ApiRequestCreatedPoliciesByUserGraph';
import { getLast5Months } from '../../data/months';
import { debounce } from 'lodash';

const options = {
    chart: {
        animations: {
            enabled: false, // Desactivar las animaciones
        },
        height: 350,
        maxWidth: "100%",
        type: "area",
        fontFamily: "Inter, sans-serif",
        dropShadow: {
            enabled: false,
        },
        toolbar: {
            show: false,
        },
    },
    tooltip: {
        z: {
            formatter: function (seriesName, opts) {
                if (opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex] === 0) {
                    return undefined;
                }
                return seriesName;
            }
        }
    },
    fill: {
        type: "gradient",
        gradient: {
            opacityFrom: 0.55,
            opacityTo: 0,
            shade: "#1C64F2",
            gradientToColors: ["#1C64F2"],
        },
    },
    dataLabels: {
        enabled: false,
    },
    stroke: {
        curve: "smooth",
        width: 2,
    },
    grid: {
        show: false,
    },
    markers: {
        size: 0, // Desactivar los marcadores de datos
    },
    series: [],
    xaxis: {
        categories: [],
        labels: {
            show: true,
            style: {
                colors: '#9CA3AF',
            }
        },
        axisBorder: {
            show: false,
        },
        axisTicks: {
            show: false,
        },
    },
    yaxis: {
        show: true,
        labels: {
            show: true,
            style: {
                colors: '#9CA3AF',
            }
        },
    },
};  

export const PoliciesCreatedByUser = () => {
    const chartRef = useRef(null);
    const { apiData, isLoading } = ApiRequestCreatedPoliciesByUserGraph();
    const [search, setSearch] = useState('')

    const last5Months = getLast5Months();
    const graphData = useMemo(() => apiData && Array.isArray(apiData) ? apiData : [], [apiData]);
    const filteredGraphData = graphData.filter(user => user.name.toLowerCase().includes(search.toLowerCase()));
    const series = filteredGraphData.map(user => ({ name: user.name, data: user.data }));

    const debouncedUpdateSeries = useMemo(() => debounce((series) => {
        if (chartRef.current) {
            chartRef.current.updateSeries(series);
        }
    }, 300), []);

    useLayoutEffect(() => {
        if (!isLoading && apiData) {
            if (chartRef.current) {
                debouncedUpdateSeries(series);
                chartRef.current.updateOptions({ xaxis: { categories: last5Months } });
            } else {
                options.series = series;
                options.xaxis.categories = last5Months;
                chartRef.current = new ApexCharts(document.getElementById("chartByPoliciesCreated"), options);
                chartRef.current.render();
            }
        }
    }, [apiData, isLoading, series, last5Months, debouncedUpdateSeries]);

    return (
        <div>
            {isLoading ?
                <div className="flex justify-center items-center">
                    <div role="status">
                        <svg aria-hidden="true" className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                        </svg>
                    </div>
                </div> :
                <div>
                    <div id="chartByPoliciesCreated"></div>
                    <div className="relative w-full">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <svg aria-hidden="true" className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input type="search" id="simple-search-policies" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar usuario..." />
                    </div>
                </div>
            }
        </div>
    );
}